<script setup lang="ts">
    import { excelImportCodeSet } from '@/shared/helpers/api';
    import { getReleaseLink } from '@/shared/helpers/routing';
    import { requireCanCreateCodeImport as vRequireCanCreateCodeImport } from '@/shared/directives/requirePermission';
    import useBuefy from '@/shared/composables/useBuefy';
    import { useRouter } from 'vue-router';
    import { http } from '@/shared/httpWrapper';
    import useShow from '@/shared/composables/useShow.js';

    const router = useRouter();
    const buefy = useBuefy();
    const show = useShow();

    function triggerImportCodeSet(): void {
        document.getElementById('fileId')!.click();
    }

    async function importCodeSet(e: Event): Promise<void> {
        const files = (e.target as HTMLInputElement).files;

        if (files === null || !files.length)
            return;

        const formData = new FormData();
        formData.append('file', files[0]);

        const loadingComponent = buefy.loading.open({});

        try {
            const response = await excelImportCodeSet(http, formData);
            const releaseId = response.data.releaseId;
            await router.push(getReleaseLink(releaseId));
        } catch (err) {
            show.showError(err);
        } finally {
            loadingComponent.close();
        }
    }
</script>

<template>
    <div>
        <h1 class="title">
            <b-icon icon="file-import-outline" />
            Import library content
        </h1>
        <hr>
        <div class="container is-max-desctop is-size-5 content">
            <h2>
                Import file format
            </h2>
            <p>
                CommonLibrary accepts Excel files with a particular format that varies based on
                the particular library it belong to. The easiest way to create a file for import
                is to export a library content set. This will have the correct structure and metadata for
                import to the same library set.
            </p>
            <p>
                <img
                    src="/images/excel-import-example.png"
                    alt="Example Excel import file">
            </p>
            <h2>
                Instructions
            </h2>
            <p>
                Click the import button below and select the Excel file you want to import.
                You will then be sent to a release page where you can review the changes
                implied by the imported file.
            </p>
            <p>
                If you are satisfied with the change description
                you can then <b>verify</b>, <b>commit</b>, and <b>publish</b> them.
            </p>

            <div class="block has-text-centered">
                <input
                    id="fileId"
                    type="file"
                    hidden
                    @change="importCodeSet">
                <b-button
                    v-require-can-create-code-import
                    type="is-info"
                    size="is-large"
                    @click="triggerImportCodeSet">
                    <i class="mdi mdi-upload" />
                    Select import file
                </b-button>
            </div>

            <hr>
            <h2>Requirements</h2>
            <ul>
                <li>The import file must have a <b>Name</b> column, except for relation libraries (see below).</li>
                <li>All attributes included in item identities must have their own columns.</li>
                <li>Other columns are optional.</li>
            </ul>

            <h2>Relation libraries</h2>
            <p>
                For relation libraries, the <b>Name</b> and <b>Description</b> columns are optional. Name is always
                auto-generated from the two identity CodeRef values and any user-provided Name is ignored.
                Description is auto-generated only for new codes; if a Description column is included, that value
                is used instead.
            </p>
            <p>
                The generated Name and Description follow the pattern:<br>
                <code>{lib1}-{codeRef1Name}_{lib2}-{codeRef2Name}</code><br>
                <code>{lib1}-{codeRef1Name} relates to {lib2}-{codeRef2Name}</code>
            </p>

            <h2>How-To: Working with import files</h2>
            <ul>
                <li>
                    <b>Marking records as deleted:</b>
                    <br>
                    You cannot delete existing records in a library set via an import. To remove a record, set the attribute <code>IsValid</code> to <code>FALSE</code>.
                </li>
                <li>
                    <b>Using exported files as templates:</b>
                    <br>
                    You may remove data rows from an exported file and use it as a template for import without risking data deletion.
                </li>
                <li>
                    <b>Deleting individual values:</b>
                    <br>
                    To delete a value from a specific field, do not clear the cell &mdash; instead, enter <code>{null}</code> (including the curly brackets).
                </li>
            </ul>

            <h2>Other notes</h2>
            <ul>
                <li>
                    It is possible to include data for more than one library set in a single import file, provided you place each set in a separate sheet.
                </li>
            </ul>
        </div>
    </div>
</template>
